import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import axios from "axios";
import { 
  Calendar,
  ArrowLeft,
  Newspaper
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const BlogPost = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/${slug}`);
      setPost(response.data);
    } catch (err) {
      // Demo posts
      const demoPosts = {
        'cross-border-trade-uae-east-africa': {
          title: 'Cross-Border Trade: UAE to East Africa Opportunities',
          content: `
            <p>The trade corridor between the United Arab Emirates and East Africa represents one of the most dynamic and growing commercial relationships in the global marketplace. With billions of dollars in annual trade volume, businesses on both ends are discovering unprecedented opportunities for growth and collaboration.</p>
            
            <h2>Key Markets</h2>
            <p>East African markets including Kenya, Uganda, Tanzania, Ethiopia, and Rwanda have shown consistent economic growth over the past decade. This growth has created demand for:</p>
            <ul>
              <li>Electronics and consumer goods</li>
              <li>Construction materials and machinery</li>
              <li>Textiles and apparel</li>
              <li>Food products and beverages</li>
              <li>Automotive parts and vehicles</li>
            </ul>
            
            <h2>Trade Advantages</h2>
            <p>The UAE's strategic position as a global trading hub offers several advantages for East African buyers:</p>
            <ul>
              <li>Access to products from across Asia, Europe, and the Middle East</li>
              <li>Established shipping routes and logistics infrastructure</li>
              <li>Competitive pricing due to tax-free zones</li>
              <li>Multi-currency trading capabilities</li>
            </ul>
            
            <h2>Getting Started</h2>
            <p>For businesses looking to engage in UAE-East Africa trade, AfriGulf Market provides a secure platform connecting verified suppliers with serious buyers. Our RFQ (Request for Quotation) system ensures transparent pricing, while our Trade Assurance program protects both parties in every transaction.</p>
          `,
          cover_image_path: 'https://images.unsplash.com/photo-1649519764734-85758bda0302?w=1200&q=80',
          created_at: '2024-01-15T00:00:00Z'
        },
        'understanding-trade-finance-letters-of-credit': {
          title: 'Understanding Trade Finance: Letters of Credit Explained',
          content: `
            <p>Letters of Credit (LC) remain one of the most important financial instruments in international trade. Understanding how they work can help both buyers and sellers navigate cross-border transactions with confidence.</p>
            
            <h2>What is a Letter of Credit?</h2>
            <p>A Letter of Credit is a payment guarantee issued by a bank on behalf of a buyer. It ensures that the seller will receive payment as long as they meet the conditions specified in the LC.</p>
            
            <h2>Types of Letters of Credit</h2>
            <ul>
              <li><strong>Sight LC:</strong> Payment is made immediately upon presentation of compliant documents</li>
              <li><strong>Usance LC:</strong> Payment is deferred for a specified period (e.g., 30, 60, or 90 days)</li>
              <li><strong>Confirmed LC:</strong> A second bank adds its guarantee to the LC</li>
              <li><strong>Standby LC:</strong> Acts as a backup payment method if the buyer fails to pay</li>
            </ul>
            
            <h2>Key Documents Required</h2>
            <p>Common documents required for LC payment include:</p>
            <ul>
              <li>Commercial Invoice</li>
              <li>Bill of Lading</li>
              <li>Packing List</li>
              <li>Certificate of Origin</li>
              <li>Insurance Certificate</li>
            </ul>
            
            <h2>Tips for Successful LC Transactions</h2>
            <p>1. Ensure all document details match exactly as specified in the LC</p>
            <p>2. Submit documents within the validity period</p>
            <p>3. Work with experienced freight forwarders and banks</p>
            <p>4. Keep copies of all documents for your records</p>
          `,
          cover_image_path: 'https://images.unsplash.com/photo-1583737077382-3f51318d6074?w=1200&q=80',
          created_at: '2024-01-10T00:00:00Z'
        },
        'shipping-routes-dubai-mombasa': {
          title: 'Shipping Routes: Optimizing Logistics from Dubai to Mombasa',
          content: `
            <p>The shipping route from Dubai to Mombasa is one of the busiest trade lanes connecting the Gulf to East Africa. Understanding the logistics involved can help businesses optimize their supply chain and reduce costs.</p>
            
            <h2>Port Overview</h2>
            <p><strong>Jebel Ali Port (Dubai):</strong> One of the largest container ports in the world, handling over 15 million TEUs annually. It offers excellent connectivity and free zone benefits.</p>
            <p><strong>Mombasa Port (Kenya):</strong> The largest port in East Africa, serving as the gateway for goods destined for Kenya, Uganda, Rwanda, and beyond.</p>
            
            <h2>Transit Times</h2>
            <p>Typical transit times from Dubai to Mombasa range from 7 to 14 days depending on the shipping line and routing:</p>
            <ul>
              <li>Direct services: 7-10 days</li>
              <li>Transshipment services: 12-14 days</li>
            </ul>
            
            <h2>Cost Factors</h2>
            <p>Several factors affect shipping costs:</p>
            <ul>
              <li>Container type (20ft, 40ft, refrigerated)</li>
              <li>Season and demand fluctuations</li>
              <li>Fuel prices and surcharges</li>
              <li>Documentation and handling fees</li>
            </ul>
            
            <h2>Incoterms to Consider</h2>
            <p>Common trade terms for this route include:</p>
            <ul>
              <li><strong>FOB (Free on Board):</strong> Seller delivers to the port of origin</li>
              <li><strong>CIF (Cost, Insurance, Freight):</strong> Seller covers cost and freight to destination</li>
              <li><strong>DDP (Delivered Duty Paid):</strong> Seller handles all costs to buyer's location</li>
            </ul>
          `,
          cover_image_path: 'https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?w=1200&q=80',
          created_at: '2024-01-05T00:00:00Z'
        }
      };
      
      if (demoPosts[slug]) {
        setPost(demoPosts[slug]);
      }
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-8 w-32 mb-8" />
        <Skeleton className="h-64 w-full rounded-md mb-8" />
        <Skeleton className="h-10 w-3/4 mb-4" />
        <Skeleton className="h-4 w-1/4 mb-8" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container-main py-16 text-center">
        <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" aria-hidden="true" />
        <h2 className="text-xl font-semibold mb-2">Article not found</h2>
        <Link to="/blog">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('blog.backToList')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {/* Hero */}
      <div 
        className="h-64 md:h-96 bg-cover bg-center relative"
        style={{
          backgroundImage: post.cover_image_path 
            ? `url('${post.cover_image_path}')`
            : 'linear-gradient(to br, #0D4F8B, #10B981)'
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <div className="container-main py-8">
        <div className="max-w-3xl mx-auto -mt-32 relative z-10">
          <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
            {t('blog.backToList')}
          </Link>

          <article className="bg-white rounded-md shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
              <Calendar className="w-4 h-4" aria-hidden="true" />
              {new Date(post.created_at).toLocaleDateString()}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {post.title}
            </h1>
            
            <div 
              className="prose prose-slate max-w-none prose-headings:font-semibold prose-headings:text-slate-900 prose-a:text-[#0D4F8B]"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </article>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;


